// <s-metal-chart>: live EUR price and 1-year history for gold, silver, platinum or palladium from metalpriceapi.com.
// The latest point is the live spot price, shared with the header price bar (localStorage, ~3 min) and
// refreshed on the bar's "gv:price-refresh" event, so the charts always match the price bar.
// History (1 year, or up to 5 with data-years) is shared by every chart on the page and cached for 6 hours.
// Hovering or touching the chart shows the price for that day.

const CACHE_KEY = 'gv-metal-history-v3';
const CACHE_TTL = 6 * 60 * 60 * 1000;
const LATEST_KEY = 'gv-spot-ticker-latest';
const LATEST_TTL = 170 * 1000;
const KEYS = { gold: 'EURXAU', silver: 'EURXAG', platinum: 'EURXPT', palladium: 'EURXPD' };
const RANGE_DAYS = { '1w': 7, '1m': 31, '3m': 92, '6m': 183, '1y': 365, '5y': 1827 };
const GRAMS_PER_OZ = 31.1034768;

const isoDate = (date) => date.toISOString().split('T')[0];
const historyRequests = new Map();

/**
 * Daily EUR rates for the last `years` years (1 or more). Past days never change, so the history is cached
 * in localStorage for 6 hours and shared by every chart on the page; today's point comes from the live price.
 * The API allows at most 365 days per request, so longer histories are split into yearly ranges.
 */
function loadHistory(apiKey, years = 1) {
  if (historyRequests.has(years)) return historyRequests.get(years);
  const key = `${CACHE_KEY}-${years}`;
  const request = (async () => {
    try {
      const cached = JSON.parse(localStorage.getItem(key) || 'null');
      if (cached && Date.now() - cached.time < CACHE_TTL && cached.rates) return cached.rates;
    } catch {}
    const ranges = [];
    let end = new Date();
    for (let i = 0; i < years; i++) {
      const start = new Date(end);
      start.setDate(start.getDate() - 364);
      ranges.push([isoDate(start), isoDate(end)]);
      end = new Date(start);
      end.setDate(end.getDate() - 1);
    }
    try {
      const parts = await Promise.all(
        ranges.map(async ([s, e]) => {
          const url = `https://api.metalpriceapi.com/v1/timeframe?start_date=${s}&end_date=${e}&api_key=${encodeURIComponent(apiKey)}&base=EUR&currencies=XAU,XAG,XPT,XPD`;
          const response = await fetch(url);
          const data = await response.json();
          if (!data.success || !data.rates) throw new Error('rates');
          return data.rates;
        }),
      );
      const rates = Object.assign({}, ...parts);
      try {
        localStorage.setItem(key, JSON.stringify({ time: Date.now(), rates }));
      } catch {}
      return rates;
    } catch {
      historyRequests.delete(years);
      return null;
    }
  })();
  historyRequests.set(years, request);
  return request;
}

let latestRequest = null;
let latestRequestTime = 0;

/** Live spot prices; reuses the header price bar's cached response when it is fresh. */
function loadLatest(apiKey) {
  if (latestRequest && Date.now() - latestRequestTime < 30 * 1000) return latestRequest;
  latestRequestTime = Date.now();
  latestRequest = (async () => {
    try {
      const cached = JSON.parse(localStorage.getItem(LATEST_KEY) || 'null');
      if (cached && Date.now() - cached.time < LATEST_TTL && cached.data) return cached.data;
    } catch {}
    try {
      const url = `https://api.metalpriceapi.com/v1/latest?api_key=${encodeURIComponent(apiKey)}&base=EUR&currencies=XAU,XAG,XPT,XPD`;
      const response = await fetch(url);
      const data = await response.json();
      if (!data.success || !data.rates) return null;
      try {
        localStorage.setItem(LATEST_KEY, JSON.stringify({ time: Date.now(), data }));
      } catch {}
      return data;
    } catch {
      return null;
    }
  })();
  return latestRequest;
}

function priceFrom(rates, key) {
  // API returns either EURXAU (EUR per ounce) or XAU (ounces per EUR); support both.
  const direct = Number(rates?.[key]);
  const inverse = Number(rates?.[key.slice(3)]);
  return Number.isFinite(direct) && direct > 0 ? direct : inverse > 0 ? 1 / inverse : NaN;
}

class MetalChart extends HTMLElement {
  #controller = null;
  #series = [];
  #daily = [];
  #liveTime = null;
  #range = '1y';
  #points = [];
  #minMax = null;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.addEventListener(
      'click',
      (event) => {
        const button = event.target.closest('[data-chart-range]');
        if (!button || !this.contains(button)) return;
        this.#range = button.dataset.chartRange;
        this.querySelectorAll('[data-chart-range]').forEach((b) => b.setAttribute('aria-pressed', String(b === button)));
        this.#render();
      },
      { signal },
    );
    // Wait a moment so the price bar's fresh response is cached first and both share one request.
    window.addEventListener('gv:price-refresh', () => setTimeout(() => this.#applyLatest(), 3000), { signal });
    const plot = this.querySelector('[data-chart-plot]');
    plot?.addEventListener('pointermove', (event) => this.#showPoint(event), { signal });
    plot?.addEventListener('pointerdown', (event) => this.#showPoint(event), { signal });
    plot?.addEventListener('pointerleave', () => this.#hidePoint(), { signal });
    this.#range = this.querySelector('[data-chart-range][aria-pressed="true"]')?.dataset.chartRange || '1y';
    this.#load();
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  async #load() {
    const apiKey = this.dataset.apiKey;
    const key = KEYS[this.dataset.metal] || KEYS.gold;
    const years = Math.max(1, Math.min(5, Number.parseInt(this.dataset.years, 10) || 1));
    const rates = apiKey ? await loadHistory(apiKey, years) : null;
    if (!this.isConnected) return;
    if (!rates) return this.#status(this.dataset.errorText || '');
    const todayIso = isoDate(new Date());
    this.#series = Object.keys(rates)
      .filter((d) => d <= todayIso)
      .sort()
      .map((d) => {
        // API returns either EURXAU (EUR per ounce) or XAU (ounces per EUR); support both.
        const direct = Number(rates[d]?.[key]);
        const inverse = Number(rates[d]?.[key.slice(3)]);
        const price = Number.isFinite(direct) && direct > 0 ? direct : inverse > 0 ? 1 / inverse : NaN;
        return [d, price];
      })
      .filter(([, price]) => Number.isFinite(price) && price > 0);
    this.#daily = this.#series;
    if (this.#series.length < 2) return this.#status(this.dataset.errorText || '');
    this.#render();
    await this.#applyLatest();
  }

  /** Replaces today's daily point with the live spot price and re-renders. */
  async #applyLatest() {
    const apiKey = this.dataset.apiKey;
    if (!apiKey || this.#daily.length < 2) return;
    const latest = await loadLatest(apiKey);
    if (!this.isConnected || !latest) return;
    const key = KEYS[this.dataset.metal] || KEYS.gold;
    const price = priceFrom(latest.rates, key);
    if (!Number.isFinite(price) || price <= 0) return;
    const stamp = Number(latest.timestamp) > 0 ? new Date(latest.timestamp * 1000) : new Date();
    const day = isoDate(stamp);
    this.#series = [...this.#daily.filter(([d]) => d < day), [day, price]];
    this.#liveTime = stamp;
    this.#render();
  }

  #status(message) {
    const el = this.querySelector('[data-chart-status]');
    if (!el) return;
    el.textContent = message;
    el.classList.toggle('hidden', message === '');
    el.classList.toggle('flex', message !== '');
  }

  #money(value, digits = 2) {
    return new Intl.NumberFormat('sk-SK', {
      style: 'currency',
      currency: this.dataset.currency || 'EUR',
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    }).format(value);
  }

  #render() {
    const all = this.#series;
    if (all.length < 2) return;
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - RANGE_DAYS[this.#range]);
    const cutoffIso = isoDate(cutoff);
    let points = all.filter(([d]) => d >= cutoffIso);
    if (points.length < 2) points = all.slice(-2);

    const [lastDate, last] = points[points.length - 1];
    const first = points[0][1];
    const change = ((last - first) / first) * 100;

    this.querySelector('[data-chart-price]').textContent = this.#money(last);
    this.querySelector('[data-chart-gram]').textContent = this.#money(last / GRAMS_PER_OZ);
    const kgEl = this.querySelector('[data-chart-kg]');
    if (kgEl) kgEl.textContent = this.#money((last / GRAMS_PER_OZ) * 1000, 0);
    this.#points = points;
    this.#hidePoint();
    const changeEl = this.querySelector('[data-chart-change]');
    const pct = new Intl.NumberFormat('sk-SK', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(change);
    changeEl.textContent = `${change > 0 ? '+' : ''}${pct} %`;
    changeEl.style.color = change >= 0 ? 'var(--color-success)' : 'var(--color-critical)';
    const dateEl = this.querySelector('[data-chart-date]');
    if (this.#liveTime && isoDate(this.#liveTime) === lastDate) {
      const time = this.#liveTime.toLocaleTimeString('sk-SK', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Bratislava' });
      const date = this.#liveTime.toLocaleDateString('sk-SK', { timeZone: 'Europe/Bratislava' });
      dateEl.textContent = `${this.dataset.liveLabel || ''} ${date} ${time}`.trim();
    } else {
      dateEl.textContent = new Date(lastDate).toLocaleDateString('sk-SK');
    }

    const values = points.map(([, v]) => v);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const span = max - min || 1;
    const width = 320;
    const height = 180;
    const pad = 12;
    const line = points
      .map(([, v], i) => {
        const x = (i / (points.length - 1)) * width;
        const y = pad + (1 - (v - min) / span) * (height - pad * 2);
        return `${x.toFixed(1)},${y.toFixed(1)}`;
      })
      .join(' ');
    this.querySelector('[data-chart-line]').setAttribute('points', line);
    this.querySelector('[data-chart-area]').setAttribute('points', `0,${height} ${line} ${width},${height}`);
    this.querySelector('[data-chart-max]').textContent = this.#money(max, 0);
    this.querySelector('[data-chart-min]').textContent = this.#money(min, 0);
    const startEl = this.querySelector('[data-chart-start]');
    if (startEl) startEl.textContent = new Date(points[0][0]).toLocaleDateString('sk-SK');
    this.#minMax = { min, span, pad, height };
    this.#status('');
  }

  /** Crosshair + tooltip with the date and price of the day under the pointer. */
  #showPoint(event) {
    const points = this.#points;
    const plot = this.querySelector('[data-chart-plot]');
    const tip = this.querySelector('[data-chart-tip]');
    const cross = this.querySelector('[data-chart-cross]');
    if (!plot || !tip || !cross || points.length < 2 || !this.#minMax) return;
    const rect = plot.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
    const index = Math.round(ratio * (points.length - 1));
    const [date, value] = points[index];
    const x = (index / (points.length - 1)) * 100;
    const { min, span, pad, height } = this.#minMax;
    const y = ((pad + (1 - (value - min) / span) * (height - pad * 2)) / height) * 100;
    cross.style.left = `${x}%`;
    cross.hidden = false;
    const dot = this.querySelector('[data-chart-dot]');
    if (dot) {
      dot.style.left = `${x}%`;
      dot.style.top = `${y}%`;
      dot.hidden = false;
    }
    tip.querySelector('[data-tip-date]').textContent = new Date(date).toLocaleDateString('sk-SK');
    tip.querySelector('[data-tip-price]').textContent = this.#money(value);
    tip.hidden = false;
    const flip = x > 60;
    tip.style.left = flip ? '' : `calc(${x}% + 0.75rem)`;
    tip.style.right = flip ? `calc(${100 - x}% + 0.75rem)` : '';
  }

  #hidePoint() {
    this.querySelectorAll('[data-chart-tip], [data-chart-cross], [data-chart-dot]').forEach((el) => {
      el.hidden = true;
    });
  }
}

if (!customElements.get('s-metal-chart')) customElements.define('s-metal-chart', MetalChart);
